/**
 * Poner la contraseña nueva, con el token que viene en la URL.
 *
 * El token sale del querystring del enlace del correo. Si falta, no se
 * muestra el formulario: llenarlo para que después falle es hacerle perder
 * el tiempo a la persona.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Alert } from '../components/Alert';
import { AuthLayout } from '../components/AuthLayout';
import { Field } from '../components/Field';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';
import { MIN_PASSWORD_LENGTH } from './registerForm';

export function ResetPasswordPage(): JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooShort = password.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm !== password;
  const canSubmit = !tooShort && !mismatch && confirm !== '' && !sending;

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitted(true);
    setError(null);
    if (!canSubmit) return;
    setSending(true);
    try {
      // Solo la contraseña nueva y el token. La confirmación se quedó acá.
      await api.resetPassword({ token, newPassword: password });
      setDone(true);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  if (token === '') {
    return (
      <AuthLayout>
        <h1>{t('reset.title')}</h1>
        <Alert kind="error">{t('reset.noToken')}</Alert>
        <div className="links">
          <Link to="/forgot-password">{t('login.forgot')}</Link>
        </div>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout>
        <h1>{t('reset.doneTitle')}</h1>
        <Alert kind="success">{t('reset.doneBody')}</Alert>
        <div className="links">
          <Link to="/login">{t('common.backToLogin')}</Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h1>{t('reset.title')}</h1>
      <p className="lead">{t('reset.lead')}</p>
      {error !== null && <Alert kind="error">{error}</Alert>}

      <form onSubmit={(event) => void onSubmit(event)} noValidate>
        <Field
          id="reset-password"
          label={t('reset.password')}
          help={t('register.passwordHelp')}
          error={submitted && tooShort ? t('validation.passwordShort') : undefined}
        >
          {(control) => (
            <input
              {...control}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>

        <Field
          id="reset-confirm"
          label={t('reset.passwordConfirm')}
          help={t('register.passwordConfirmHelp')}
          error={submitted && mismatch ? t('validation.passwordMismatch') : undefined}
        >
          {(control) => (
            <input
              {...control}
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          )}
        </Field>

        <button type="submit" disabled={!canSubmit}>
          {sending ? t('common.sending') : t('reset.submit')}
        </button>
      </form>
    </AuthLayout>
  );
}
